import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { Question } from '../../../../datasources/questions/QuestionsDataSource';
import { Basket } from '../../../../types/basket/Basket';
import { Flow, FlowType } from '../../../../types/flow/Flow';
import { Modal } from '../../../../shared/ui/Modal';
import { TextInput } from '../../../../shared/ui/TextInput';
import {
  Footer,
  Section,
  SectionLabel,
  SectionTitle,
} from '../../../../shared/ui/Modal/Body';
import { FLOW_TYPES } from './flowBuilder.helpers';
import {
  FlowStep,
  eligibleTriggers,
  isStepsValid,
  moveStep,
  removeStep,
  stepsToDefinition,
  validateSteps,
} from './flowSteps';
import { buildPreviewQuestionnaire } from './flowPreview';
import { FlowStepCard } from './FlowStepCard';
import { FlowPreviewPanel } from './FlowPreviewPanel';

const PREVIEW_STORAGE_KEY = 'basket-flow-editor-preview';

function readPreviewPreference(): boolean {
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY) !== 'closed';
  } catch {
    return true;
  }
}

function savePreviewPreference(open: boolean): void {
  try {
    localStorage.setItem(PREVIEW_STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    // Preferência apenas de conveniência; sem storage a prévia abre por padrão.
  }
}

type FlowEditorDialogProps = {
  editorOpen: boolean;
  onClose: () => void;
  saving: boolean;
  editorError?: string;
  editingFlow: Flow | null;
  code: string;
  setCode: (code: string) => void;
  type: FlowType;
  setType: (type: FlowType) => void;
  steps: FlowStep[];
  setSteps: Dispatch<SetStateAction<FlowStep[]>>;
  questions: Question[];
  questionsByCode: Map<string, Question>;
  baskets: Basket[];
  saveFlow: () => void;
};

export const FlowEditorDialog = ({
  editorOpen,
  onClose,
  saving,
  editorError,
  editingFlow,
  code,
  setCode,
  type,
  setType,
  steps,
  setSteps,
  questions,
  questionsByCode,
  baskets,
  saveFlow,
}: FlowEditorDialogProps) => {
  const [previewOpen, setPreviewOpen] = useState(readPreviewPreference);
  const [searchKey, setSearchKey] = useState(0);
  const stacked = useMediaQuery('(max-width: 1100px)');

  useEffect(() => {
    savePreviewPreference(previewOpen);
  }, [previewOpen]);

  const validation = useMemo(
    () => validateSteps(steps, questionsByCode),
    [steps, questionsByCode]
  );
  const pendingCount =
    validation.steps.filter(Boolean).length + (validation.general ? 1 : 0);
  const canSave = Boolean(code.trim()) && isStepsValid(validation) && !saving;

  const availableQuestions = questions.filter(
    (question) =>
      question.active &&
      (question.is_loop ||
        !steps.some((step) => step.questionCode === question.code))
  );

  const questionnaire = useMemo(
    () =>
      buildPreviewQuestionnaire(
        stepsToDefinition(steps),
        questions,
        baskets,
        code,
        type
      ),
    [steps, questions, baskets, code, type]
  );

  function addQuestion(question: Question | null): void {
    if (!question) return;
    setSteps((current) => [...current, { questionCode: question.code }]);
    setSearchKey((key) => key + 1);
  }

  function updateStep(index: number, step: FlowStep): void {
    setSteps((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? step : item))
    );
  }

  const scrollable = stacked
    ? {}
    : { overflowY: 'auto' as const, minHeight: 0 };

  return (
    <Modal
      open={editorOpen}
      handleClose={() => !saving && onClose()}
      width="96vw"
      height="90vh"
      sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 700, color: '#444' }}>
          {editingFlow ? `Editar fluxo ${editingFlow.code}` : 'Novo fluxo'}
        </span>
        <Button
          variant="outlined"
          size="small"
          startIcon={previewOpen ? <EyeOff size={16} /> : <Eye size={16} />}
          onClick={() => setPreviewOpen((open) => !open)}
        >
          {previewOpen ? 'Ocultar prévia' : 'Mostrar prévia'}
        </Button>
      </div>

      {editorError && (
        <Alert severity="error" style={{ marginBottom: 12 }}>
          {editorError}
        </Alert>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          gap: 24,
          ...(stacked
            ? { overflowY: 'auto', display: 'flex', flexDirection: 'column' }
            : {
                display: 'grid',
                gridTemplateColumns: previewOpen
                  ? 'minmax(0, 45fr) minmax(0, 55fr)'
                  : 'minmax(0, 1fr)',
              }),
        }}
      >
        <div style={{ ...scrollable, paddingRight: 4 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Section>
              <SectionTitle>Identificação</SectionTitle>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
                  gap: 12,
                }}
              >
                <TextInput
                  label="Nome do fluxo"
                  value={code}
                  handleChange={(event) =>
                    setCode(event.target.value.toUpperCase())
                  }
                  inputProps={{ maxLength: 50 }}
                  helperText="Exemplo: FLUXO_O2_ADULTO"
                  required
                  disabled={saving}
                />
                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={type}
                  onChange={(event) => setType(event.target.value as FlowType)}
                  helperText="Ainda sem efeito no pedido."
                  disabled={saving}
                >
                  {FLOW_TYPES.map((flowType) => (
                    <MenuItem key={flowType.value} value={flowType.value}>
                      {flowType.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </Section>

            <Section>
              <SectionTitle>Perguntas do atendimento</SectionTitle>
              <SectionLabel>
                A ordem da lista é a ordem em que as perguntas aparecem no
                pedido
              </SectionLabel>
              <Autocomplete
                key={searchKey}
                options={availableQuestions}
                getOptionLabel={(question) => question.text}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                onChange={(_, question) => addQuestion(question)}
                noOptionsText="Nenhuma pergunta disponível"
                disabled={saving}
                renderOption={(props, question) => (
                  <li {...props} key={question.code}>
                    <div>
                      <div>{question.text}</div>
                      <Typography variant="caption" color="text.secondary">
                        {question.code}
                      </Typography>
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Adicionar pergunta"
                    placeholder="Busque pelo texto da pergunta"
                  />
                )}
              />

              {steps.length === 0 ? (
                <Paper
                  variant="outlined"
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    borderStyle: 'dashed',
                  }}
                >
                  <Typography color="text.secondary">
                    Nenhuma pergunta ainda. Busque acima para começar.
                  </Typography>
                </Paper>
              ) : (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  {steps.map((step, index) => (
                    <FlowStepCard
                      key={`${step.questionCode}-${index}`}
                      index={index}
                      step={step}
                      question={questionsByCode.get(step.questionCode)}
                      triggers={eligibleTriggers(steps, index, questionsByCode)}
                      questionsByCode={questionsByCode}
                      baskets={baskets}
                      error={validation.steps[index]}
                      isLast={index === steps.length - 1}
                      disabled={saving}
                      onChange={(next) => updateStep(index, next)}
                      onMove={(offset) =>
                        setSteps((current) =>
                          moveStep(
                            current,
                            index,
                            index + offset,
                            questionsByCode
                          )
                        )
                      }
                      onRemove={() =>
                        setSteps((current) =>
                          removeStep(current, index, questionsByCode)
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </Section>
          </div>
        </div>

        {previewOpen && editorOpen && (
          <div
            style={{
              ...scrollable,
              background: '#f9fafb',
              borderRadius: 12,
              padding: 16,
            }}
          >
            <FlowPreviewPanel questionnaire={questionnaire} />
          </div>
        )}
      </div>

      <Footer>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {pendingCount > 0 && steps.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {pendingCount === 1
                ? '1 pergunta precisa de ajuste'
                : `${pendingCount} perguntas precisam de ajuste`}
            </Typography>
          )}
          <Button variant="contained" onClick={saveFlow} disabled={!canSave}>
            {saving ? 'Salvando...' : 'Salvar fluxo'}
          </Button>
        </div>
      </Footer>
    </Modal>
  );
};
